import { useCallback, useEffect, useRef, useState } from "react";

// Generic infinite-scroll pagination.
// `fetcher(skip, limit)` must return `{ items, hasMore }`.
// Returns a `sentinelRef` — render an element with it at the bottom of the list;
// when it scrolls into view the next page loads automatically.
export function usePagination(fetcher, { limit = 24, deps = [] } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const busyRef = useRef(false);
  const lenRef = useRef(0);
  const reqRef = useRef(0); // lot 695 — jeton : une réponse périmée (filtre changé pendant le chargement) est ignorée
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async (reset) => {
    // Un rechargement (filtre / vue changés) supplante toujours la requête en cours ; « charger plus » attend.
    if (!reset && busyRef.current) return;
    const id = ++reqRef.current;
    busyRef.current = true;
    const skip = reset ? 0 : lenRef.current;
    if (reset) { setLoading(true); setLoadingMore(false); } else setLoadingMore(true);
    try {
      const { items: batch = [], hasMore: more = false } = await fetcherRef.current(skip, limit);
      if (id !== reqRef.current) return;
      setItems((prev) => {
        const next = reset ? batch : [...prev, ...batch];
        lenRef.current = next.length;
        return next;
      });
      setHasMore(!!more);
    } catch {
      if (id !== reqRef.current) return;
      if (reset) { setItems([]); lenRef.current = 0; setHasMore(false); }
    }
    if (reset) setLoading(false); else setLoadingMore(false);
    busyRef.current = false;
  }, [limit]);

  // Reset & reload whenever the caller's deps change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(true); }, deps);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !busyRef.current) run(false);
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadingMore, run]);

  const patchItems = useCallback((updater) => {
    setItems((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      lenRef.current = next.length;
      return next;
    });
  }, []);

  return { items, patchItems, loading, loadingMore, hasMore, sentinelRef, reload: () => run(true) };
}
